import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Edit3,
  Save,
  X,
  Calendar,
  Users,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-toastify";

const GroupInfoSection = ({ group, onUpdate, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [groupName, setGroupName] = useState(group.group_name);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!groupName.trim()) {
      toast.error("Group name cannot be empty");
      return;
    }

    if (groupName.trim() === group.group_name) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      await onUpdate({ group_name: groupName.trim() });
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update group name");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setGroupName(group.group_name);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isProcessing = !group.uploaded_at && group.number_of_contacts === null;
  const contactCount = group.number_of_contacts || 0;

  return (
    <div className="space-y-6">
      {/* Group Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
             Contact  Group Information
            </h2>
            <p className="mt-1 text-gray-600">
              Manage your contact group details
            </p>
          </div>

          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex gap-2 items-center px-4 py-2 text-blue-600 bg-blue-50 rounded-lg transition-colors hover:bg-blue-100"
            >
              <Edit3 className="w-4 h-4" />
              <span className="text-sm font-medium">Edit</span>
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Group Name */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
             Name of Group
            </label>
            {isEditing ? (
              <div className="flex gap-3">
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300 transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter group name"
                  disabled={saving}
                />
                <button
                  onClick={handleSave}
                  disabled={saving || !groupName.trim()}
                  className="flex gap-2 items-center px-4 py-3 text-white bg-blue-600 rounded-xl transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">Save</span>
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex gap-2 items-center px-4 py-3 text-gray-600 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  <span className="text-sm font-medium">Cancel</span>
                </button>
              </div>
            ) : (
              <div className="px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                <span className="font-medium text-gray-900">
                  {group.group_name}
                </span>
              </div>
            )}
          </div>

          {/* Group Stats Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Total Contacts
                  </p>
                  <p className="text-2xl font-bold text-blue-900">
                    {isProcessing ? "..." : contactCount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-50 rounded-xl border border-red-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">Created</p>
                  <p className="text-sm font-medium text-red-900">
                    {formatDate(group.created_at)}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-purple-100 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-purple-800">
                    Group ID
                  </p>
                  <p className="font-mono text-sm text-purple-900">
                    {group.contact_group_slug}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-orange-800">
                    Group Type
                  </p>
                  <p className="text-sm font-medium text-orange-900">
                    {contactCount === 0
                      ? "Empty"
                      : contactCount < 100
                      ? "Small"
                      : contactCount < 1000
                      ? "Medium"
                      : "Large"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Processing Status */}
          {isProcessing && (
            <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-yellow-100 rounded-lg">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="w-5 h-5"
                  >
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </motion.div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-yellow-800">
                    Processing Contacts
                  </p>
                  <p className="text-sm text-yellow-700">
                    Your contacts are being processed. This may take a few
                    minutes for large files.
                  </p>
                </div>
                <button
                  onClick={onRefresh}
                  className="px-4 py-2 text-yellow-700 bg-yellow-100 rounded-lg transition-colors hover:bg-yellow-200"
                >
                  Check Status
                </button>
              </div>
            </div>
          )}

          {/* Success Status */}
          {!isProcessing && contactCount > 0 && (
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">Ready to Use</p>
                  <p className="text-sm text-green-700">
                    Your contact group is ready for SMS campaigns and exports.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Empty Status */}
          {!isProcessing && contactCount === 0 && (
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-gray-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">No Contacts</p>
                  <p className="text-sm text-gray-700">
                    Add contacts to this group to start sending SMS messages.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default GroupInfoSection;
